<template>
  <div v-if="employmentId">
    <StaffDocumentsPanel
      @edit-expiry="openExpiryDialog"
      @review-status="openApproveDialog"
      @upload="openUploadDialog"
      :attachments="attachments"
      :can-review-documents="canReviewDocuments"
    />

    <StaffDocumentUploadDialog
      v-if="uploadDialogVisible"
      @close="uploadDialogVisible = false"
      @uploaded="handleAttachmentChange"
      ref="uploadDialogRef"
      :business-id="businessId"
      :categories="staffDocumentCategories"
      :employment-id="employmentId"
      :initial-category="uploadCategory"
      :self-upload="selfService"
    />

    <StaffDocumentApproveDialog
      v-if="approveDialogVisible && canReviewDocuments"
      @approve="approveAttachment"
      @close="closeApproveDialog"
      @deny="denyAttachment"
      ref="approveDialogRef"
      :category-name="reviewRow?.category"
      :processing="dialogProcessing"
    />

    <StaffDocumentExpiryDialog
      v-if="expiryDialogVisible"
      @close="expiryDialogVisible = false"
      @save="saveExpiryDate"
      ref="expiryDialogRef"
      :initial-expires-at="expiryRow?.expiresAt"
    />
  </div>
</template>

<script setup>
import { STAFF_DOCUMENT_CATEGORIES, formatExpiryNote } from '@/shared/utils/staffDocumentUtils';
import StaffDocumentApproveDialog from '@/shared/components/provider/staff-documents/StaffDocumentApproveDialog.vue';
import StaffDocumentExpiryDialog from '@/shared/components/provider/staff-documents/StaffDocumentExpiryDialog.vue';
import StaffDocumentsPanel from '@/shared/components/provider/staff-documents/StaffDocumentsPanel.vue';
import StaffDocumentUploadDialog from '@/shared/components/provider/staff-documents/StaffDocumentUploadDialog.vue';
import useApi from '@/shared/composables/useApi';

const props = defineProps({
  businessId: {
    type: String,
    default: null,
  },
  canReviewDocuments: {
    type: Boolean,
    default: false,
  },
  employmentId: {
    type: String,
    default: null,
  },
  selfService: {
    type: Boolean,
    default: false,
  },
});

const { api } = useApi();

const attachments = ref([]);
const uploadDialogVisible = ref(false);
const approveDialogVisible = ref(false);
const expiryDialogVisible = ref(false);
const uploadDialogRef = ref(null);
const approveDialogRef = ref(null);
const expiryDialogRef = ref(null);
const uploadCategory = ref(null);
const reviewRow = ref(null);
const expiryRow = ref(null);
const dialogProcessing = ref(false);

const staffDocumentCategories = STAFF_DOCUMENT_CATEGORIES;

watch(
  () => props.employmentId,
  () => {
    void loadAttachments();
  },
  { immediate: true },
);

function attachmentIndexApi() {
  if (props.canReviewDocuments) return api.organization.attachment;

  return api.manager.attachment;
}

function attachmentUpdateApi() {
  if (props.canReviewDocuments) return api.organization.attachment;
  if (props.selfService) return api.member.attachment;

  return api.manager.attachment;
}

async function loadAttachments() {
  if (!props.employmentId) {
    attachments.value = [];
    return;
  }

  const response = await attachmentIndexApi().index({
    owner_id: props.employmentId,
    owner_type: 'Employment',
  });

  if (response?.status !== 200) return;

  attachments.value = response.data;
}

async function handleAttachmentChange() {
  uploadDialogVisible.value = false;
  await loadAttachments();
}

async function openUploadDialog() {
  uploadCategory.value = null;
  uploadDialogVisible.value = true;
  await nextTick();
  uploadDialogRef.value?.open();
}

async function openApproveDialog(row) {
  if (!props.canReviewDocuments) return;

  reviewRow.value = row;
  approveDialogVisible.value = true;
  await nextTick();
  approveDialogRef.value?.open();
}

function closeApproveDialog() {
  approveDialogVisible.value = false;
  reviewRow.value = null;
}

async function openExpiryDialog(row) {
  expiryRow.value = row;
  expiryDialogVisible.value = true;
  await nextTick();
  expiryDialogRef.value?.open(row.expiresAt);
}

async function approveAttachment() {
  if (!props.canReviewDocuments || !reviewRow.value?.attachment?.id) return;

  dialogProcessing.value = true;
  const response = await api.organization.attachment.update(reviewRow.value.attachment.id, {
    status: 'Accepted',
  });
  dialogProcessing.value = false;

  if (response?.status !== 200) return;

  closeApproveDialog();
  await loadAttachments();
}

async function denyAttachment() {
  if (!props.canReviewDocuments || !reviewRow.value?.attachment?.id) return;

  dialogProcessing.value = true;
  const response = await api.organization.attachment.update(reviewRow.value.attachment.id, {
    status: 'Rejected',
  });
  dialogProcessing.value = false;

  if (response?.status !== 200) return;

  closeApproveDialog();
  await loadAttachments();
}

async function saveExpiryDate(expiresAt) {
  const attachmentId = expiryRow.value?.attachment?.id;
  if (!attachmentId) return;

  const note = formatExpiryNote(expiresAt);
  if (!note) return;

  const response = await attachmentUpdateApi().update(attachmentId, { note });

  if (response?.status !== 200) return;

  expiryDialogRef.value?.close();
  expiryDialogVisible.value = false;
  expiryRow.value = null;
  await loadAttachments();
}
</script>
